import { supabase } from '@/lib/supabase';
import { useStore } from '@/lib/store';
import { extractProfileData } from '@/lib/store/constants';
import { intervalsClient } from '@/lib/api/intervals-client';
import type { AthleteProfile, ScheduledWorkout, WeekSchedule, TrainingBlock, SubscriptionStatus, SubscriptionTier, FeatureFlag, ProSource, RaceEvent, WeeklyAvailability, PlanState, EnabledSports } from '@/types';
import type { GamificationState } from '@/lib/gamification/types';
import { calculateAgeFromBirthdate, normalizeBirthdate, resolveAthleteAge } from '@/lib/athlete/age';
import {
    beginBootstrapSyncForUser,
    canWriteCloudForUser,
    endBootstrapSyncForUser,
    markBootstrapReadyForUser,
} from '@/services/syncLifecycle';
import {
    normalizeWeeklyCheckinPreferences,
    toWeeklyCheckinPreferencesRow,
} from '@/lib/checkin/preferences';
import { ANONYMOUS_SUBMISSION_ID_KEY, getTransientItem, removeTransientItem } from '@/lib/storage/transient';
import { normalizePreferences } from '@/lib/store/normalizers';
import { createLogger } from '@/lib/logger';
import { stravaActivityService } from '@/services/stravaActivityService';
import { adaptivePlanner } from '@/services/adaptive-planner';
import { ExternalServiceError } from '@/lib/errors';
import { runIncrementalActivitySync } from '@/services/activity-sync-service';
import { supportsFeatureAccessLevel } from '@/lib/feature-flags';
import { mergeTriathlonProgression } from '@/lib/triathlon/weekly-rhythm';
import { buildProfileDiscoveryState, parseProfileDiscoveryState } from '@/lib/discovery/profile-state';
import { hasActiveOnboardingProgress } from '@/lib/first-run-preferences';
import {
    extractMissingProfileColumn,
    PROFILE_OPTIONAL_COLUMNS,
    type ProfileOptionalColumn,
} from '@/services/profileSchemaFallback';

const log = createLogger('cloudSync');

// ============================================================================
// CHANGE DETECTION HELPERS
// ============================================================================

interface SyncChange {
    field: string;
    label: string;
    oldSummary: string;
    newSummary: string;
    severity: 'info' | 'warning' | 'destructive';
}

function summarizePlan(plan: WeekSchedule[]): string {
    if (!plan || plan.length === 0) return 'No plan';
    const totalWorkouts = plan.reduce((sum, week) =>
        sum + week.days.filter(d => d.workout || d.secondary).length, 0);
    return `${plan.length} weeks, ${totalWorkouts} workouts`;
}

function summarizeBlocks(blocks: TrainingBlock[]): string {
    if (!blocks || blocks.length === 0) return 'No blocks';
    return `${blocks.length} training blocks`;
}

function plansAreDifferent(local: WeekSchedule[], cloud: WeekSchedule[]): boolean {
    if (local.length !== cloud.length) return true;
    if (local.length === 0) return false;
    // Compare week numbers and basic structure
    for (let i = 0; i < local.length; i++) {
        if (local[i].weekNumber !== cloud[i].weekNumber) return true;
        if (local[i].phase !== cloud[i].phase) return true;
        // Compare workout count per week
        const localCount = local[i].days.filter(d => d.workout).length;
        const cloudCount = cloud[i].days.filter(d => d.workout).length;
        if (localCount !== cloudCount) return true;
    }
    return false;
}

/**
 * Returns true if the zones object has at least one non-empty zone array.
 * Used to avoid uploading default/empty zones that would overwrite populated cloud data.
 */
function hasNonEmptyZones(zones: unknown): boolean {
    if (!zones || typeof zones !== 'object') return false;
    for (const sport of Object.values(zones as Record<string, unknown>)) {
        if (!sport || typeof sport !== 'object') continue;
        for (const arr of Object.values(sport as Record<string, unknown>)) {
            if (Array.isArray(arr) && arr.length > 0) return true;
        }
    }
    return false;
}

function parseNumber(value: unknown): number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim().length > 0) {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return undefined;
}

function parseGender(value: unknown): AthleteProfile['gender'] | undefined {
    if (value === 'male' || value === 'female' || value === 'other' || value === 'prefer_not_to_say') {
        return value;
    }
    return undefined;
}

function preferPersistedValue<T>(localValue: T | null | undefined, persistedValue: T | null | undefined): T | null {
    if (localValue === undefined || localValue === null) {
        return persistedValue ?? null;
    }

    if (typeof localValue === 'string' && localValue.trim().length === 0) {
        return (persistedValue as T | null | undefined) ?? null;
    }

    return localValue;
}

interface FeatureFlagRow {
    feature_key: string;
    description: string;
    is_pro_only: boolean;
    min_tier: SubscriptionTier;
    is_enabled: boolean;
    access_level?: string | null;
}

function mapFeatureFlagRows(rows: FeatureFlagRow[]): Record<string, FeatureFlag> {
    const flagsMap: Record<string, FeatureFlag> = {};

    rows.forEach((flag) => {
        flagsMap[flag.feature_key] = {
            key: flag.feature_key,
            description: flag.description,
            isProOnly: flag.is_pro_only,
            minTier: flag.min_tier,
            isEnabled: flag.is_enabled ?? true,
            accessLevel: supportsFeatureAccessLevel(flag.feature_key)
                && typeof flag.access_level === 'string'
                ? flag.access_level as FeatureFlag['accessLevel']
                : undefined,
        };
    });

    return flagsMap;
}

export const syncPublicFeatureFlags = async () => {
    const { data: flagsData, error: flagsError } = await supabase
        .from('feature_flags')
        .select('*');

    if (flagsError) {
        log.error('syncPublicFlags', 'Failed to fetch public feature flags', { error: flagsError });
        throw flagsError;
    }

    if (!flagsData) return;

    const currentFlags = useStore.getState().featureFlags;
    useStore.getState().setFeatureFlags({
        ...currentFlags,
        ...mapFeatureFlagRows(flagsData as FeatureFlagRow[]),
    });
};

function parseGymAccess(value: unknown): AthleteProfile['gymAccess'] | undefined {
    if (value === 'gym' || value === 'no_gym') return value;
    return undefined;
}

type ActivePlanLike = {
    id: string;
    updated_at?: string | null;
    created_at?: string | null;
    plan_structure?: unknown;
    goal_race_date?: string | null;
};

function parseJsonSafely(value: unknown): unknown {
    if (typeof value !== 'string') return value;
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseWeekScheduleArray(value: unknown): WeekSchedule[] {
    const parsed = parseJsonSafely(value);
    return Array.isArray(parsed) ? (parsed as WeekSchedule[]) : [];
}

function parseSettingsObject(value: unknown): Record<string, unknown> {
    const parsed = parseJsonSafely(value);
    return isRecord(parsed) ? parsed : {};
}

function selectCanonicalActivePlan<T extends ActivePlanLike>(plans: T[]): { primary: T | null; duplicates: T[] } {
    if (!plans.length) return { primary: null, duplicates: [] };
    const sorted = [...plans].sort((a, b) => {
        const aTs = Date.parse(a.updated_at || a.created_at || '') || 0;
        const bTs = Date.parse(b.updated_at || b.created_at || '') || 0;
        return bTs - aTs;
    });
    const [primary, ...duplicates] = sorted;
    return { primary: primary || null, duplicates };
}

async function archiveDuplicateActivePlans(planIds: string[]): Promise<void> {
    if (!planIds.length) return;
    const { error } = await supabase
        .from('training_plans')
        .update({
            status: 'archived',
            updated_at: new Date().toISOString(),
        })
        .in('id', planIds);

    if (error) {
        log.warn('archive', 'Failed to archive duplicate active plans', { error });
    } else {
        log.warn('archive', `Archived ${planIds.length} duplicate active plan(s).`, { count: planIds.length });
    }
}

// ============================================================================
// LOCAL -> CLOUD (Upload)
// ============================================================================

// UPLOAD OPTIONS
interface UploadOptions {
    includePlan?: boolean;
    includeSettings?: boolean;
    includeProfile?: boolean;
    allowDuringBootstrap?: boolean;
}

type UploadSkipReason = 'hydrating' | 'unauthenticated' | 'bootstrap_not_ready' | 'local_only_profile';

export type UploadResult =
    | { status: 'success' }
    | { status: 'partial_failure'; errors: { step: string; error: unknown }[] }
    | { status: 'skipped'; reason: UploadSkipReason };

export const uploadLocalDataToCloud = async (
    options: UploadOptions = {
        includePlan: true,
        includeSettings: true,
        includeProfile: true,
        allowDuringBootstrap: false,
    }
): Promise<UploadResult> => {
    const state = useStore.getState();

    // Guard: never upload while cloud → local hydration is in progress.
    // The auto-sync timer (set during local store rehydration) can fire mid-download,
    // which would read partially-applied state and overwrite cloud with stale data.
    if (_isHydrating || state.isHydrating) {
        log.debug('upload', 'Skipping upload — cloud hydration in progress.');
        return { status: 'skipped', reason: 'hydrating' };
    }
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
        log.warn('upload', 'User not logged in, skipping cloud sync.');
        return { status: 'skipped', reason: 'unauthenticated' };
    }

    if (!options.allowDuringBootstrap && !canWriteCloudForUser(user.id)) {
        log.debug('upload', 'Skipping upload — bootstrap sync not yet complete for user.');
        return { status: 'skipped', reason: 'bootstrap_not_ready' };
    }

    // ALLOW IMPERSONATION SYNC:
    // Coaches need to maintain write access to athlete profiles.
    // The targetUserId logic below correctly identifies the athlete's ID.
    // We assume RLS allows the update.
    if (state.isSupportImpersonation) {
        log.warn('upload', 'Impersonating user, skipping cloud sync to prevent overwrite.');
        return { status: 'skipped', reason: 'local_only_profile' };
    }


    // Determine Target User ID
    const activeProfile = state.profiles.find(p => p.id === state.activeProfileId);
    let targetUserId = user.id;

    if (activeProfile && activeProfile.relation === 'coached' && activeProfile.id) {
        if (activeProfile.id.length > 20 && !activeProfile.id.startsWith('local-')) {
            targetUserId = activeProfile.id;
            log.info('upload', 'Syncing as Coach for Athlete', { targetUserId });
        } else {
            log.info('upload', 'Local-only profile, skipping cloud sync.');
            return { status: 'skipped', reason: 'local_only_profile' };
        }
    }

    // Determine if this is a coached profile (NOT our own profile)
    const isCoachedProfileSync = targetUserId !== user.id;

    // Isolate each sync operation so a failure in one (e.g. plan) does not
    // prevent others (e.g. profile, metrics) from completing. Errors are
    // collected and reported at the end rather than short-circuiting (I4 fix).
    const syncErrors: { step: string; error: unknown }[] = [];

    // 1. Sync Profile
    if (options.includeProfile || options.includeSettings) {
        try {
            // Fetch existing profile first to avoid wiping unselected fields if we are doing a partial update
            const { data: existingProfile, error: existingProfileError } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', targetUserId)
                .maybeSingle();
            if (existingProfileError) throw existingProfileError;
            if (isCoachedProfileSync && !existingProfile) {
                throw new Error(`Coached profile ${targetUserId} not found or not accessible to coach.`);
            }
            const currentSettings = existingProfile?.plan_settings && typeof existingProfile.plan_settings === 'object'
                ? existingProfile.plan_settings
                : {};
            const nextDiscoveryState = buildProfileDiscoveryState(
                existingProfile?.discovery_state,
                state.discovery,
                state.preferences.hasSeenWalkthrough
            );

            // Construct Settings Payload
            let newSettings = { ...currentSettings };

            if (options.includeSettings) {
                if (isCoachedProfileSync) {
                    log.info('upload', 'Coached profile detected - preserving existing API key, not overwriting with Coach credentials');
                }

                newSettings = {
                    ...newSettings,
                    ...(state.planSettings || {}),
                    _syncedData: {
                        // Preserve existing synced data if not overwriting plan
                        ...(currentSettings._syncedData || {}),
                        availability: state.availability,
                        blocks: options.includePlan ? state.blocks : (currentSettings._syncedData?.blocks || []), // Only sync blocks if syncing plan
                        events: state.events,
                        goal: state.goal,
                        progression: state.progression,
                        cyclingProgression: state.cyclingProgression,
                        config: state.config,
                        enabledSports: state.enabledSports,
                        triathlonProgression: state.triathlonProgression,
                        featureFlags: state.featureFlags,
                        enforcePremium: state.enforcePremium,
                        paidFeatures: state.paidFeatures,
                        stravaConfig: {
                            clientId: import.meta.env.VITE_STRAVA_CLIENT_ID || state.stravaConfig.clientId,
                            clientSecret: import.meta.env.VITE_STRAVA_CLIENT_SECRET || state.stravaConfig.clientSecret,
                        },
                        // Only overwrite thresholdPace if local has a value;
                        // prevents undefined from erasing a previously-saved cloud value.
                        ...(state.athlete.thresholdPace != null ? { thresholdPace: state.athlete.thresholdPace } : {}),
                        // Backward-compatible profile fallback fields.
                        // If newer profile columns are missing on a target DB, these
                        // values can still be restored from plan_settings._syncedData.
                        height: state.athlete.height ?? null,
                        gender: state.athlete.gender ?? null,
                        birthdate: state.athlete.birthdate ?? null,
                        age: state.athlete.age ?? null,
                        gymAccess: state.athlete.gymAccess ?? null,
                        maxStrengthSessionsPerWeek: state.athlete.maxStrengthSessionsPerWeek,
                        preferredWorkoutsPerWeek: state.athlete.preferredWorkoutsPerWeek ?? null,
                        preferences: state.preferences,
                        gamification: state.gamification,
                        planState: state.planState,
                        planName: state.planName,
                        consent: state.consent,
                        // intervalsApiKey: intervalsApiKeyToSync, // REMOVED: Now stored in encrypted column via RPC
                        // Only overwrite cloud zones if local has actual zone data;
                        // prevents default empty arrays from wiping populated cloud zones.
                        ...(hasNonEmptyZones(state.athlete.zones) ? { zones: state.athlete.zones } : {}),
                        metrics: state.metrics || null,
                        discovery: state.discovery,
                    }
                };
                log.info('upload', 'Profile payload updated with zones', {
                    hasNonEmpty: hasNonEmptyZones(state.athlete.zones),
                    runningZones: state.athlete.zones?.running?.power?.length,
                    cyclingZones: state.athlete.zones?.cycling?.power?.length,
                    swimmingZones: state.athlete.zones?.swimming?.swimPace?.length,
                });
            }

            // Construct Profile Payload
            const targetProfile = state.profiles.find(p => p.id === targetUserId)
                || state.profiles.find(p => p.id === state.activeProfileId);
            const profilePayload: Record<string, unknown> = {
                id: targetUserId,
                updated_at: new Date().toISOString(),
                // intervals_api_key: intervalsApiKeyToSync, // HIDDEN: Now handled by secure RPC
                plan_settings: newSettings,
                ...((options.includeProfile || options.includeSettings) ? { discovery_state: nextDiscoveryState } : {}),
                ...(options.includeProfile ? {
                    ftp: preferPersistedValue(state.metrics.ftp, existingProfile?.ftp),
                    lthr: preferPersistedValue(
                        state.metrics.lthrBpm ?? state.metrics.runningLthrBpm,
                        existingProfile?.lthr
                    ),
                    weight: preferPersistedValue(state.athlete.weight, existingProfile?.weight),
                    height: preferPersistedValue(state.athlete.height, existingProfile?.height),
                    gender: preferPersistedValue(state.athlete.gender, existingProfile?.gender),
                    dob: preferPersistedValue(state.athlete.birthdate, existingProfile?.dob),
                    email: preferPersistedValue(
                        state.athlete.email,
                        existingProfile?.email ?? (!isCoachedProfileSync ? user.email : null)
                    ),
                    display_name: preferPersistedValue(state.athlete.name, existingProfile?.display_name),
                    sport: preferPersistedValue(state.athlete.sport, existingProfile?.sport),
                    experience: preferPersistedValue(state.athlete.experience, existingProfile?.experience),
                    gym_access: preferPersistedValue(state.athlete.gymAccess, existingProfile?.gym_access),
                    consent: state.consent,
                    max_weekly_hours: preferPersistedValue(
                        state.athlete.maxWeeklyTrainingHours,
                        existingProfile?.max_weekly_hours
                    ),
                    long_run_day: preferPersistedValue(state.longRunDay, existingProfile?.long_run_day),
                    checkin_preferences: toWeeklyCheckinPreferencesRow(
                        normalizeWeeklyCheckinPreferences(state.preferences.weeklyCheckIn)
                    ),
                    intervals_athlete_id: preferPersistedValue(
                        state.intervals.athleteId || targetProfile?.intervalsAthleteId,
                        existingProfile?.intervals_athlete_id
                    ),
                    profile_picture: preferPersistedValue(
                        state.athlete.profilePicture,
                        existingProfile?.profile_picture
                    ),
                    connected_at: preferPersistedValue(
                        targetProfile?.connectedAt,
                        existingProfile?.connected_at
                    ),
                } : {})
            };

            const writeProfilePayload = async (payload: Record<string, unknown>) => {
                if (targetUserId !== user.id) {
                    // If we are a coach updating an athlete, we only have UPDATE permissions, not INSERT.
                    // Upsert requires INSERT permissions even if the row exists in some configurations.
                    // Since we know the athlete profile exists (we selected it above), we use update().
                    return supabase
                        .from('profiles')
                        .update(payload)
                        .eq('id', targetUserId);
                }
                // Self-update can use upsert
                return supabase
                    .from('profiles')
                    .upsert(payload);
            };

            let payloadToWrite = { ...profilePayload };
            const strippedColumns = new Set<ProfileOptionalColumn>();
            let writeError: unknown = null;

            for (let attempt = 0; attempt <= PROFILE_OPTIONAL_COLUMNS.length; attempt++) {
                const { error: profileError } = await writeProfilePayload(payloadToWrite);
                if (!profileError) {
                    writeError = null;
                    break;
                }

                const missingColumn = extractMissingProfileColumn(profileError);
                if (!missingColumn || !(missingColumn in payloadToWrite)) {
                    writeError = profileError;
                    break;
                }

                strippedColumns.add(missingColumn);
                const { [missingColumn]: _ignored, ...nextPayload } = payloadToWrite;
                payloadToWrite = nextPayload;
                writeError = profileError;
            }

            if (writeError) throw writeError;

            if (strippedColumns.size > 0) {
                log.warn('upload', 'Profile sync succeeded with schema fallback', {
                    missingColumns: Array.from(strippedColumns).join(', '),
                });
            }

            // --- SECURE API KEY SYNC MOVED OR REMOVED ---
            // If this is our OWN profile, we update the encrypted key via RPC - REMOVED for OAuth flow
        } catch (err) {
            syncErrors.push({ step: 'profile', error: err });
        }
    }

    // Persist OAuth tokens in a dedicated step so they can still be saved even if
    // profile writes fall back or partially fail on schema drift.
    if (!isCoachedProfileSync && (options.includeProfile || options.includeSettings)) {
        try {
            const { accessToken, refreshToken, expiresAt, scope } = state.intervals;
            if (accessToken) {
                log.info('upload', 'Updating encrypted OAuth tokens via RPC');
                const { error: oauthRpcError } = await supabase.rpc('set_user_intervals_oauth_tokens', {
                    p_access_token: accessToken,
                    p_refresh_token: refreshToken ?? '',
                    p_expires_at: expiresAt ?? 0,
                    p_scope: scope ?? '',
                });
                if (oauthRpcError) throw oauthRpcError;
                log.info('upload', 'OAuth tokens stored securely.');
            }
        } catch (err) {
            syncErrors.push({ step: 'oauth_tokens', error: err });
        }
    }

    // Persist Strava OAuth tokens
    if (!isCoachedProfileSync && (options.includeProfile || options.includeSettings)) {
        try {
            const { accessToken, refreshToken, expiresAt } = state.strava;
            if (accessToken) {
                log.info('upload', 'Updating encrypted Strava tokens via RPC');
                const { error: stravaRpcError } = await supabase.rpc('set_user_strava_oauth_tokens', {
                    p_access_token: accessToken,
                    p_refresh_token: refreshToken ?? '',
                    p_expires_at: expiresAt ?? 0,
                    p_scope: 'read,activity:read_all,activity:write',
                });
                if (stravaRpcError) throw stravaRpcError;
                log.info('upload', 'Strava tokens stored securely.');
            }
        } catch (err) {
            syncErrors.push({ step: 'strava_tokens', error: err });
        }
    }

    // 2. Sync Active Training Plan
    if (options.includePlan && state.plan && state.plan.length > 0) {
        try {
            const planPayload = {
                user_id: targetUserId,
                plan_structure: JSON.stringify(state.plan),
                start_date: state.config?.startDate || new Date().toISOString(),
                goal_race_date: state.goal?.raceDate || null,
                status: 'active',
                updated_at: new Date().toISOString(),
            };

            const { data: existingPlans, error: fetchError } = await supabase
                .from('training_plans')
                .select('id,updated_at,created_at')
                .eq('user_id', targetUserId)
                .eq('status', 'active'); // Removed .single() to avoid error

            if (fetchError) {
                log.error('upload', 'Error checking for existing plan', { error: fetchError });
                throw fetchError;
            }

            const { primary, duplicates } = selectCanonicalActivePlan(existingPlans || []);
            const planId = primary?.id || null;

            if (duplicates.length > 0) {
                log.warn('upload', `Found ${duplicates.length + 1} active plans for user. Keeping newest and archiving duplicates.`, { targetUserId, duplicateCount: duplicates.length });
                await archiveDuplicateActivePlans(duplicates.map(p => p.id));
            }

            const { error: planError } = await supabase
                .from('training_plans')
                .upsert(planId ? { ...planPayload, id: planId } : planPayload);

            if (planError) {
                log.error('upload', 'Failed to upsert training plan', { error: planError });
                throw planError;
            }
        } catch (err) {
            syncErrors.push({ step: 'plan', error: err });
        }
    }

    // 3. Sync Completed Workouts
    try {
        const completedWorkouts: Record<string, unknown>[] = [];
        state.plan.forEach(week => {
            week.days.forEach(day => {
                const processWorkout = (workout: ScheduledWorkout | null) => {
                    // Logbook Fix: Sync ALL workouts, not just completed ones.
                    // This allows the Logbook to show "Plan vs Actual".
                    if (workout) {
                        completedWorkouts.push({
                            // Logbook Fix: Use instanceId as the stable ID for reliable upserts
                            // This allows multiple workouts per day (e.g. Run AM + Run PM) without
                            // conflict, and ensures updates overwrite the correct row.
                            id: workout.instanceId,
                            user_id: targetUserId,
                            date: workout.date,
                            type: workout.sport || 'Run',
                            planned_duration: (workout.estimatedDuration || 0) * 60,
                            planned_tss: workout.estimatedTSS || workout.plannedTSS || 0,
                            actual_duration: (workout.actualDuration || 0) * 60,
                            actual_tss: workout.actualTSS || 0,
                            distance: workout.distance || 0,
                            rpe: workout.rpe || null,
                            feeling: workout.feeling || null,
                            notes: workout.notes || null,
                            // Persist completion status so we can filter later if needed
                            // (though standard Supabase 'workouts' table doesn't have a status column check schema?)
                            // Schema check showed: id, user_id, date, feeling, strava_activity_id, etc.
                            // We might want to save completion status if the schema supported it, 
                            // but for now, we just want the record there.
                        });
                    }
                };
                processWorkout(day.workout);
                processWorkout(day.secondary);
            });
        });

        if (completedWorkouts.length > 0) {
            // Logbook Fix: Clear existing workouts in the date range before upserting 
            // the current plan. This prevents orphans from appearing as duplicates 
            // after the plan is regenerated with new instanceIds.
            const sortedDates = completedWorkouts
                .map(w => w.date as string)
                .filter(Boolean)
                .sort();

            if (sortedDates.length > 0) {
                const minDate = sortedDates[0];
                const maxDate = sortedDates[sortedDates.length - 1];

                const { error: clearError } = await supabase
                    .from('workouts')
                    .delete()
                    .eq('user_id', targetUserId)
                    .gte('date', minDate)
                    .lte('date', maxDate);

                if (clearError) log.warn('upload', 'Failed to clear orphans', { error: clearError });
            }

            const { error: workoutError } = await supabase
                .from('workouts')
                .upsert(completedWorkouts, { onConflict: 'id' });
            if (workoutError) throw workoutError;
        }
    } catch (err) {
        syncErrors.push({ step: 'workouts', error: err });
    }

    // 4. Sync Health Metrics (Current Snapshot)
    if (state.metrics) {
        try {
            const { error: metricsError } = await supabase
                .from('health_metrics')
                .upsert({
                    user_id: targetUserId,
                    date: new Date().toISOString().split('T')[0],
                    resting_hr: state.metrics.restingHr || null,
                });
            if (metricsError) throw metricsError;
        } catch (err) {
            syncErrors.push({ step: 'health_metrics', error: err });
        }
    }

    if (syncErrors.length > 0) {
        log.error('upload', `Upload partially failed (${syncErrors.length} step(s))`, {
            errors: syncErrors.map(e => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const err = e.error as any;
                const msg = err?.message || String(err);
                return `${e.step}: ${msg}${err?.details ? ` (${err.details})` : ''}`;
            }).join('; ')
        });
        return { status: 'partial_failure', errors: syncErrors };
    } else {
        log.info('upload', 'Cloud sync completed successfully.');
        return { status: 'success' };
    }
};

// ============================================================================
// CLOUD -> LOCAL (Hydration) with Conflict Detection
// ============================================================================

/**
 * Downloads cloud data and applies it locally.
 * If `interactive` is true (default), shows a warning dialog before
 * overwriting significant local data (plan, blocks, settings).
 * Non-destructive fields (name, metrics) are always merged silently.
 */
export const downloadCloudDataToLocal = async (userId: string, interactive = true) => {
    log.info('hydrate', 'Hydrating data from cloud for user', { userId });
    const store = useStore.getState();
    _isHydrating = true;
    store.setHydrating(true);

    // Identify if we are impersonating (loading someone else's data)
    const { data: { user: authUser } } = await supabase.auth.getUser();
    const isImpersonating = authUser ? authUser.id !== userId : false;

    try {
        // 1. Fetch Profile, OAuth tokens and Active Plan IN PARALLEL.
        // These were previously awaited sequentially, which made every
        // hydration pay 3-4 network round-trips back to back and was a major
        // contributor to slow app loads.
        const isOwnUser = userId === (authUser?.id || store.authUser?.id);
        const [profileResult, plansResult, oauthResult, stravaResult] = await Promise.all([
            supabase.from('profiles').select('*').eq('id', userId).single(),
            supabase.from('training_plans').select('*').eq('user_id', userId).eq('status', 'active'),
            isOwnUser ? supabase.rpc('get_user_intervals_oauth_tokens') : Promise.resolve(null),
            isOwnUser ? supabase.rpc('get_user_strava_oauth_tokens') : Promise.resolve(null),
        ]);

        const { data: profile, error: profileError } = profileResult;
        if (profileError && profileError.code !== 'PGRST116') {
            log.error('hydrate', 'Error fetching profile', { error: profileError });
        }

        // --- SECURE OAUTH TOKENS ---
        let cloudOAuthTokens: { accessToken?: string; refreshToken?: string; expiresAt?: number; scope?: string } | null = null;
        if (profile && oauthResult) {
            const { data: oauthData, error: oauthError } = oauthResult;
            if (!oauthError && oauthData) {
                cloudOAuthTokens = typeof oauthData === 'string' ? JSON.parse(oauthData) : oauthData;
            } else if (oauthError) {
                log.warn('fetchProfile', 'OAuth token decryption failed or none exists', { message: oauthError.message });
            }
        }

        // --- SECURE STRAVA TOKENS ---
        let cloudStravaTokens: { accessToken?: string; refreshToken?: string; expiresAt?: number; scope?: string } | null = null;
        if (profile && stravaResult) {
            const { data: stravaData, error: stravaError } = stravaResult;
            if (!stravaError && stravaData) {
                cloudStravaTokens = typeof stravaData === 'string' ? JSON.parse(stravaData) : stravaData;
            } else if (stravaError) {
                log.warn('fetchProfile', 'Strava token decryption failed or none exists', { message: stravaError.message });
            }
        }

        const { data: activePlans, error: planError } = plansResult;
        if (planError) {
            log.error('hydrate', 'Error fetching plan', { error: planError });
        }

        const { primary: planData, duplicates: duplicatePlans } = selectCanonicalActivePlan(activePlans || []);
        if (duplicatePlans.length > 0) {
            void archiveDuplicateActivePlans(duplicatePlans.map(p => p.id));
        }

        // Parse cloud plan
        let cloudPlan: WeekSchedule[] = [];
        let cloudGoalRaceDate: string | null = null;
        if (planData?.plan_structure) {
            cloudPlan = parseWeekScheduleArray(planData.plan_structure);
            cloudGoalRaceDate = planData.goal_race_date || null;
        }


        interface ProfileSyncData {
            blocks?: TrainingBlock[];
            events?: RaceEvent[];
            metrics?: Record<string, unknown>;
            height?: unknown;
            birthdate?: string;
            gender?: string;
            goal?: unknown;
            progression?: unknown;
            cyclingProgression?: unknown;
            config?: unknown;
            preferences?: unknown;
            consent?: unknown;
            planState?: PlanState;
            planName?: string;
            enabledSports?: EnabledSports;
            gamification?: GamificationState;
            triathlonProgression?: unknown;
            availability?: WeeklyAvailability;
            featureFlags?: Record<string, import('@/types').FeatureFlag>;
            enforcePremium?: boolean;
            paidFeatures?: import('@/lib/store/types').AppState['paidFeatures'];
            resetKeyOnLogin?: boolean;
            gymAccess?: unknown;
            age?: number;
            thresholdPace?: number;
            maxStrengthSessionsPerWeek?: number;
            preferredWorkoutsPerWeek?: number | null;
            zones?: unknown;
            discovery?: import('@/lib/store/types').AppState['discovery'];
            stravaConfig?: import('@/types').StravaConfig;
        }

        // Parse synced data from cloud
        let syncedData: ProfileSyncData | null = null;
        let cleanSettings: Record<string, unknown> = {};
        const persistedDiscoveryState = parseProfileDiscoveryState(profile?.discovery_state);
        if (profile?.plan_settings) {
            const settings = parseSettingsObject(profile.plan_settings);
            syncedData = isRecord(settings._syncedData) ? settings._syncedData : null;
            cleanSettings = { ...settings };
            delete cleanSettings._syncedData;

            // Re-inject minimalistic _syncedData for Profile Persistence
            // We ONLY need featureFlags here because other synced data (events, etc)
            // is hydrated into state separately and Profile doesn't have fields for them.
            // But verify types first.
            if (syncedData && typeof syncedData === 'object' && 'featureFlags' in syncedData) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const flags = (syncedData as any).featureFlags;
                if (flags) {
                    cleanSettings._syncedData = { featureFlags: flags };
                }
            }
        }

        // Cloud blocks from synced data
        const cloudBlocks: TrainingBlock[] = Array.isArray(syncedData?.blocks)
            ? (syncedData?.blocks as TrainingBlock[])
            : [];

        // STRICT MODE: If interactive is FALSE (forced hydration), we skip conflict checks
        // and just wipe/replace. This is used for login/session restore.
        if (!interactive) {
            log.info('hydrate', 'Non-interactive hydration. Overwriting local state with Cloud data.');
            // Reset store to initial state first to ensure no ghost data lingers
            // BUT we must preserve the 'user' role/id which might be needed? 
            // Actually, the `applyNonDestructiveData` sets it back.
            // Let's just Apply.
        }

        // ================================================================
        // DETECT CONFLICTS (Only if interactive)
        // ================================================================
        const changes: SyncChange[] = [];
        if (interactive) {
            const localPlan = store.plan;
            const localBlocks = store.blocks;

            // Check plan conflict

            if (plansAreDifferent(localPlan, cloudPlan || [])) {
                changes.push({
                    field: 'plan',
                    label: 'Training Plan',
                    oldSummary: summarizePlan(localPlan),
                    newSummary: summarizePlan(cloudPlan || []),
                    severity: 'destructive',
                });
            }

            // Check blocks conflict
            if (localBlocks.length > 0 && cloudBlocks.length > 0 &&
                localBlocks.length !== cloudBlocks.length) {
                changes.push({
                    field: 'blocks',
                    label: 'Training Blocks (ATP)',
                    oldSummary: summarizeBlocks(localBlocks),
                    newSummary: summarizeBlocks(cloudBlocks),
                    severity: 'warning',
                });
            }
        }

        // ================================================================
        // APPLY FUNCTIONS
        // ================================================================
        // BATCHED HYDRATION: Build a single state update object instead of
        // making 15+ individual setState calls. Each individual call triggers
        // Zustand subscribers and React re-render cycles; batching them into
        // one call prevents the cascading updates that cause React error #185.
        const applyNonDestructiveData = async (nonDestructiveInteractive: boolean) => {
            const state = useStore.getState();
            if (!profile) {
                log.info('hydrate', 'Non-destructive cloud data applied (no profile).');
                return;
            }

            const batch: Record<string, unknown> = {};
            // Profile-scoped fields that also need to update the active profile entry
            const profilePatch: Record<string, unknown> = {};

            // --- Metrics ---
            const metricsPartial: Record<string, unknown> = {
                ...((syncedData?.metrics as object) || {})
            };
            if (profile.ftp) metricsPartial.ftp = profile.ftp;
            if (profile.lthr) {
                metricsPartial.lthrBpm = profile.lthr;
                metricsPartial.runningLthrBpm = profile.lthr;
            }

            const nextMetrics = { ...state.metrics, ...metricsPartial };
            if (JSON.stringify(state.metrics) !== JSON.stringify(nextMetrics)) {
                batch.metrics = nextMetrics;
                profilePatch.metrics = nextMetrics;
            }

            const resolvedCyclingFtp = parseNumber(metricsPartial.ftp);
            const ensureCyclingProgressionFtp = (base: typeof state.cyclingProgression) => {
                if (resolvedCyclingFtp == null || base.ftp === resolvedCyclingFtp) return base;
                return { ...base, ftp: resolvedCyclingFtp };
            };

            // --- Athlete ---
            // Only include fields that have actual values from the cloud.
            // Using `?? undefined` previously caused null cloud values to overwrite
            // populated local values with undefined via the spread operator.
            const athletePartial: Record<string, unknown> = {};
            if (profile.weight != null) athletePartial.weight = profile.weight;
            const profileHeight = parseNumber(profile.height);
            const syncedHeight = parseNumber(syncedData?.height);
            if (profileHeight != null) athletePartial.height = profileHeight;
            else if (syncedHeight != null) athletePartial.height = syncedHeight;

            const profileBirthdate = normalizeBirthdate(profile.dob);
            const syncedBirthdate = normalizeBirthdate(syncedData?.birthdate);
            const resolvedBirthdate = profileBirthdate || syncedBirthdate;
            if (resolvedBirthdate) {
                athletePartial.birthdate = resolvedBirthdate;
                athletePartial.age = calculateAgeFromBirthdate(resolvedBirthdate);
            }

            const profileGender = parseGender(profile.gender);
            const syncedGender = parseGender(syncedData?.gender);
            if (profileGender) athletePartial.gender = profileGender;
            else if (syncedGender) athletePartial.gender = syncedGender;
            if (profile.display_name != null) athletePartial.name = profile.display_name;
            if (profile.sport != null) athletePartial.sport = profile.sport;
            if (profile.experience != null) athletePartial.experience = profile.experience;
            const profileGymAccess = parseGymAccess(profile.gym_access);
            const syncedGymAccess = parseGymAccess(syncedData?.gymAccess);
            if (profileGymAccess) athletePartial.gymAccess = profileGymAccess;
            else if (syncedGymAccess) athletePartial.gymAccess = syncedGymAccess;
            if (profile.max_weekly_hours != null) athletePartial.maxWeeklyTrainingHours = profile.max_weekly_hours;
            if (profile.email != null) athletePartial.email = profile.email;
            if (profile.profile_picture != null) athletePartial.profilePicture = profile.profile_picture;
            if (!resolvedBirthdate && syncedData?.age != null) {
                const syncedAge = resolveAthleteAge({ age: parseNumber(syncedData.age) });
                if (syncedAge !== undefined) {
                    athletePartial.age = syncedAge;
                }
            }

            if (syncedData?.thresholdPace) {
                athletePartial.thresholdPace = syncedData.thresholdPace;
            }
            if (syncedData && (syncedData.maxStrengthSessionsPerWeek as number | undefined) !== undefined) {
                athletePartial.maxStrengthSessionsPerWeek = syncedData.maxStrengthSessionsPerWeek;
            }
            if (syncedData && (syncedData.preferredWorkoutsPerWeek as number | null | undefined) !== undefined) {
                const preferred = syncedData.preferredWorkoutsPerWeek as number | null;
                athletePartial.preferredWorkoutsPerWeek = preferred === null ? undefined : preferred;
            }
            if (syncedData?.zones && hasNonEmptyZones(syncedData.zones)) {
                // Cloud has actual zone data — deep-merge with local zones.
                const currentZones = state.athlete.zones || {
                    running: { power: [], heartRate: [], pace: [] },
                    cycling: { power: [], heartRate: [], pace: [] },
                    swimming: { power: [], heartRate: [], pace: [] },
                };
                const cloudZones = syncedData.zones as Record<string, Record<string, unknown[]>>;

                // Deep-merge: only overwrite a sport's zone arrays when the cloud
                // version is non-empty.  This prevents empty default cloud zones
                // (from a first-time upload before the user configured zones) from
                // wiping populated local zones synced via Intervals.icu.
                const mergedZones: Record<string, Record<string, unknown>> = { ...currentZones } as unknown as Record<string, Record<string, unknown>>;
                for (const sport of Object.keys(cloudZones)) {
                    const cloudSport = cloudZones[sport];
                    if (!cloudSport || typeof cloudSport !== 'object') continue;
                    const localSport = (mergedZones[sport] as Record<string, unknown>) || { power: [], heartRate: [], pace: [] };
                    const merged = { ...localSport };
                    for (const zoneType of Object.keys(cloudSport)) {
                        const arr = cloudSport[zoneType];
                        if (Array.isArray(arr) && arr.length > 0) {
                            merged[zoneType] = arr;
                        }
                    }
                    mergedZones[sport] = merged;
                }
                athletePartial.zones = mergedZones;
            }
            // If cloud zones are all-empty, skip setting athletePartial.zones
            // entirely — this preserves whatever is currently cached locally.

            const nextAthlete = { ...state.athlete, ...athletePartial };
            if (JSON.stringify(state.athlete) !== JSON.stringify(nextAthlete)) {
                batch.athlete = nextAthlete;
                profilePatch.athlete = nextAthlete;
            }

            // --- User Role ---
            // CRITICAL: Never overwrite the active user's role when impersonating.
            // A Superadmin/Coach must retain their privileges even while viewing an Athlete.
            if (profile.role && !isImpersonating && state.userRole !== profile.role) {
                batch.userRole = profile.role;
            }

            // --- Intervals Credentials ---
            const intervalsUpdate = { ...state.intervals };
            let intervalsChanged = false;
            if (profile.intervals_athlete_id && state.intervals.athleteId !== profile.intervals_athlete_id) {
                intervalsUpdate.athleteId = profile.intervals_athlete_id;
                intervalsChanged = true;
            }

            // --- RESTORE OAUTH TOKENS FROM CLOUD ---
            // If the local store has no OAuth tokens but the cloud does, restore them.
            // This enables seamless cross-device login without re-authorizing.
            if (!isImpersonating && cloudOAuthTokens) {
                const localAccess = state.intervals.accessToken;
                const localRefresh = state.intervals.refreshToken;
                const cloudAccess = cloudOAuthTokens.accessToken;
                const cloudRefresh = cloudOAuthTokens.refreshToken;
                const shouldRestoreAccess = Boolean(cloudAccess && !localAccess);
                const shouldRestoreRefresh = Boolean(cloudRefresh && !localRefresh);

                if (shouldRestoreAccess || shouldRestoreRefresh) {
                    if (shouldRestoreAccess) intervalsUpdate.accessToken = cloudAccess;
                    if (shouldRestoreRefresh) intervalsUpdate.refreshToken = cloudRefresh;
                    intervalsUpdate.expiresAt = parseNumber(cloudOAuthTokens.expiresAt);
                    intervalsUpdate.scope = cloudOAuthTokens.scope;
                    intervalsChanged = true;
                    log.info('hydrate', 'Restored OAuth tokens from cloud.');
                }
            }

            if (intervalsChanged) {
                batch.intervals = intervalsUpdate;
            }

            // --- Strava Credentials ---
            const stravaUpdate = { ...state.strava };
            let stravaChanged = false;

            // Restore or Update Strava tokens from cloud
            if (!isImpersonating && cloudStravaTokens) {
                const localAccess = state.strava.accessToken;
                const cloudAccess = cloudStravaTokens.accessToken;
                // RELAXED CONDITION: trust cloud if it has tokens and they DIFFER from local.
                // This covers:
                // 1. Missing local tokens (restoration)
                // 2. Stale local tokens (cross-device refresh)
                const shouldRestoreAccess = Boolean(cloudAccess && cloudAccess !== localAccess);

                if (shouldRestoreAccess) {
                    stravaUpdate.accessToken = cloudAccess;
                    stravaUpdate.refreshToken = cloudStravaTokens.refreshToken;
                    stravaUpdate.expiresAt = parseNumber(cloudStravaTokens.expiresAt);
                    stravaChanged = true;
                    log.info('hydrate', 'Restored/Updated Strava tokens from cloud.');
                }
            }

            if (stravaChanged) {
                batch.strava = stravaUpdate;
            }

            // --- Rest of hydrate ---

            // --- Long Run Day ---
            if (profile.long_run_day !== undefined && profile.long_run_day !== null && state.longRunDay !== profile.long_run_day) {
                batch.longRunDay = profile.long_run_day;
                profilePatch.longRunDay = profile.long_run_day;
            }

            // --- Consent ---
            if (profile.consent && typeof profile.consent === 'object') {
                const nextConsent = { ...state.consent, ...profile.consent };
                if (JSON.stringify(state.consent) !== JSON.stringify(nextConsent)) {
                    batch.consent = nextConsent;
                    profilePatch.consent = nextConsent;
                }
            }

            // --- Weekly Check-In Preferences ---
            if (profile.checkin_preferences) {
                const normalizedCheckin = normalizeWeeklyCheckinPreferences(profile.checkin_preferences);
                const nextPrefs = normalizePreferences({
                    ...state.preferences,
                    weeklyCheckIn: normalizedCheckin,
                });
                if (JSON.stringify(state.preferences) !== JSON.stringify(nextPrefs)) {
                    batch.preferences = nextPrefs;
                }
            }

            // --- Plan Settings ---
            if (Object.keys(cleanSettings).length > 0) {
                const nextPlanSettings = { ...state.planSettings, ...cleanSettings };
                if (JSON.stringify(state.planSettings) !== JSON.stringify(nextPlanSettings)) {
                    batch.planSettings = nextPlanSettings;
                    profilePatch.planSettings = nextPlanSettings;
                }
            }

            // --- Synced Data Fields ---
            if (syncedData) {
                if (syncedData.availability && JSON.stringify(state.availability) !== JSON.stringify(syncedData.availability)) {
                    batch.availability = syncedData.availability;
                    profilePatch.availability = syncedData.availability;
                }
                if (syncedData.events && Array.isArray(syncedData.events)) {
                    if (!nonDestructiveInteractive) {
                        // Fresh login: replace entirely with cloud events
                        const cloudEvents = syncedData.events as import('@/types').RaceEvent[];
                        batch.events = cloudEvents;
                        profilePatch.events = cloudEvents;
                    } else if (Array.isArray(syncedData.events) && syncedData.events.length > 0) {
                        // Interactive: merge (add cloud events not already present locally)
                        const mergedEvents = [...state.events];
                        let eventsAdded = false;
                        (syncedData.events as RaceEvent[]).forEach((event) => {
                            if (!mergedEvents.some(e => e.id === event.id)) {
                                mergedEvents.push(event);
                                eventsAdded = true;
                            }
                        });
                        if (eventsAdded) {
                            batch.events = mergedEvents;
                            profilePatch.events = mergedEvents;
                        }
                    }
                }
                if (syncedData.goal) {
                    const nextGoal = { ...state.goal, ...(syncedData.goal as object) };
                    if (JSON.stringify(state.goal) !== JSON.stringify(nextGoal)) {
                        batch.goal = nextGoal;
                        profilePatch.goal = nextGoal;
                    }
                }
                if (syncedData.progression) {
                    const nextProg = { ...state.progression, ...(syncedData.progression as object) };
                    if (JSON.stringify(state.progression) !== JSON.stringify(nextProg)) {
                        batch.progression = nextProg;
                        profilePatch.progression = nextProg;
                    }
                }
                if (syncedData.cyclingProgression) {
                    const nextCProg = ensureCyclingProgressionFtp({
                        ...state.cyclingProgression,
                        ...(syncedData.cyclingProgression as object)
                    });
                    if (JSON.stringify(state.cyclingProgression) !== JSON.stringify(nextCProg)) {
                        batch.cyclingProgression = nextCProg;
                        profilePatch.cyclingProgression = nextCProg;
                    }
                } else if (resolvedCyclingFtp != null) {
                    const nextCProg = ensureCyclingProgressionFtp(state.cyclingProgression);
                    if (JSON.stringify(state.cyclingProgression) !== JSON.stringify(nextCProg)) {
                        batch.cyclingProgression = nextCProg;
                        profilePatch.cyclingProgression = nextCProg;
                    }
                }
                if (syncedData.config && JSON.stringify(state.config) !== JSON.stringify(syncedData.config)) {
                    batch.config = syncedData.config;
                    profilePatch.config = syncedData.config;
                }
                if (syncedData.preferences) {
                    const syncedPrefs = syncedData.preferences as Record<string, unknown>;
                    const nextPrefs = normalizePreferences({
                        ...state.preferences,
                        ...syncedPrefs,
                        ...(syncedPrefs.weeklyCheckIn || syncedPrefs.weekly_check_in
                            ? {
                                weeklyCheckIn: normalizeWeeklyCheckinPreferences(
                                    syncedPrefs.weeklyCheckIn ?? syncedPrefs.weekly_check_in
                                )
                            }
                            : {}),
                    });
                    if (JSON.stringify(state.preferences) !== JSON.stringify(nextPrefs)) {
                        batch.preferences = nextPrefs;
                    }
                }
                if (syncedData.consent && typeof syncedData.consent === 'object') {
                    const nextConsent = { ...state.consent, ...(syncedData.consent as object) };
                    if (JSON.stringify(state.consent) !== JSON.stringify(nextConsent)) {
                        batch.consent = nextConsent;
                        profilePatch.consent = nextConsent;
                    }
                }
                if (syncedData.planState && JSON.stringify(state.planState) !== JSON.stringify(syncedData.planState)) {
                    batch.planState = syncedData.planState;
                    profilePatch.planState = syncedData.planState;
                }
                if (syncedData.planName) {
                    batch.planName = syncedData.planName as string;
                    profilePatch.planName = syncedData.planName;
                }
                if (syncedData.enabledSports && JSON.stringify(state.enabledSports) !== JSON.stringify(syncedData.enabledSports)) {
                    batch.enabledSports = syncedData.enabledSports;
                    profilePatch.enabledSports = syncedData.enabledSports;
                }
                if (syncedData.gamification && JSON.stringify(state.gamification) !== JSON.stringify(syncedData.gamification)) {
                    batch.gamification = syncedData.gamification;
                    profilePatch.gamification = syncedData.gamification;
                }
                if (syncedData.triathlonProgression) {
                    const nextTriProg = mergeTriathlonProgression(
                        state.triathlonProgression,
                        syncedData.triathlonProgression as Partial<typeof state.triathlonProgression>
                    );
                    if (JSON.stringify(state.triathlonProgression) !== JSON.stringify(nextTriProg)) {
                        batch.triathlonProgression = nextTriProg;
                        profilePatch.triathlonProgression = nextTriProg;
                    }
                }
                if (syncedData.featureFlags && JSON.stringify(state.featureFlags) !== JSON.stringify(syncedData.featureFlags)) {
                    batch.featureFlags = syncedData.featureFlags;
                }
                if (syncedData.enforcePremium !== undefined && state.enforcePremium !== syncedData.enforcePremium) {
                    batch.enforcePremium = syncedData.enforcePremium;
                }
                if (syncedData.paidFeatures && JSON.stringify(state.paidFeatures) !== JSON.stringify(syncedData.paidFeatures)) {
                    batch.paidFeatures = syncedData.paidFeatures;
                }
                if (syncedData.discovery && JSON.stringify(state.discovery) !== JSON.stringify(syncedData.discovery)) {
                    batch.discovery = syncedData.discovery;
                }
                if (syncedData.stravaConfig) {
                    // CRITICAL: Env vars ALWAYS take priority over cloud values.
                    // This prevents cloud hydration from wiping locally-configured credentials.
                    const envClientId = import.meta.env.VITE_STRAVA_CLIENT_ID || '';
                    const envClientSecret = import.meta.env.VITE_STRAVA_CLIENT_SECRET || '';
                    const merged = {
                        clientId: envClientId || syncedData.stravaConfig.clientId || state.stravaConfig.clientId,
                        clientSecret: envClientSecret || syncedData.stravaConfig.clientSecret || state.stravaConfig.clientSecret,
                    };
                    if (JSON.stringify(state.stravaConfig) !== JSON.stringify(merged)) {
                        batch.stravaConfig = merged;
                    }
                }
            }

            if (persistedDiscoveryState.discovery) {
                const currentDiscovery = (batch.discovery as typeof state.discovery | undefined) ?? state.discovery;
                const nextDiscovery = { ...currentDiscovery, ...persistedDiscoveryState.discovery };
                if (JSON.stringify(currentDiscovery) !== JSON.stringify(nextDiscovery)) {
                    batch.discovery = nextDiscovery;
                }
            }
            if (persistedDiscoveryState.walkthroughCompleted !== undefined) {
                const currentPreferences = (batch.preferences as typeof state.preferences | undefined) ?? state.preferences;
                const nextPreferences = normalizePreferences({
                    ...currentPreferences,
                    hasSeenWalkthrough: persistedDiscoveryState.walkthroughCompleted,
                });
                if (JSON.stringify(currentPreferences) !== JSON.stringify(nextPreferences)) {
                    batch.preferences = nextPreferences;
                }
            }

            // --- Update profiles array ONCE (instead of per-setter) ---
            // CRITICAL: We only update the profile entry that matches the 'userId' we just loaded from cloud.
            // This prevents "bleeding" data from an impersonated user into the Coach's own profile entry 
            // if activeProfileId was not switched in time.
            const profileToUpdate = state.profiles.find(p => p.id === userId);
            if (profileToUpdate) {
                batch.profiles = state.profiles.map(p => {
                    if (p.id !== userId) return p;
                    return {
                        ...p,
                        ...profilePatch,
                        isHydrated: true,
                        ...(profile.intervals_athlete_id ? { intervalsAthleteId: profile.intervals_athlete_id } : {}),
                        ...(profile.connected_at ? { connectedAt: profile.connected_at } : {}),
                    };
                });
            }

            // *** SINGLE STATE UPDATE — replaces 15+ individual setState calls ***
            // Apply to top-level state if this is the active profile, OR if no profile
            // is active yet (fresh login from incognito/new device where activeProfileId
            // is still null). The isImpersonating guard prevents coach data bleeding.
            const shouldApplyToTopLevel = state.activeProfileId === userId ||
                (!state.activeProfileId && !isImpersonating);

            if (shouldApplyToTopLevel) {
                // Bootstrap activeProfileId on fresh sessions so subsequent logic works
                if (!state.activeProfileId) {
                    batch.activeProfileId = userId;
                }
                useStore.setState(batch);
            } else if (batch.profiles) {
                // If we are pre-fetching or hydrating a background profile, just update the list
                useStore.setState({ profiles: batch.profiles as import('@/types').Profile[] });
            }

            log.info('hydrate', 'Non-destructive cloud data applied.');

            // Auto-fetch Intervals.icu profile picture if credentials are present
            // but no picture is loaded yet. Fire-and-forget (non-blocking).
            const updatedStore = useStore.getState();
            const hasCredentials = updatedStore.intervals.accessToken && updatedStore.intervals.athleteId;
            const hasPicture = updatedStore.athlete.profilePicture;
            if (hasCredentials && !hasPicture) {
                intervalsClient.testConnection().then(result => {
                    if (result.success && result.athlete?.profile_medium) {
                        // Guard: temporarily re-enable hydration flag to prevent
                        // auto-sync from uploading partial state (C5 race condition fix)
                        _isHydrating = true;
                        try {
                            const s = useStore.getState();
                            s.setAthlete({ profilePicture: result.athlete.profile_medium });

                            if (s.activeProfileId && (s.activeProfileId === userId || s.profiles.find(p => p.id === s.activeProfileId)?.intervalsAthleteId === s.intervals.athleteId)) {
                                s.updateProfileAthlete(s.activeProfileId, {
                                    profilePicture: result.athlete.profile_medium
                                });
                            } else {
                                const targetProfile = s.profiles.find(p => p.id === userId || p.intervalsAthleteId === s.intervals.athleteId);
                                if (targetProfile) {
                                    s.updateProfileAthlete(targetProfile.id, {
                                        profilePicture: result.athlete.profile_medium
                                    });
                                }
                            }

                            log.info('hydrate', 'Intervals.icu profile picture auto-fetched.');
                        } finally {
                            _isHydrating = false;
                        }
                    }
                }).catch(() => {
                    // SILENT: profile picture fetch is non-critical, failure is expected when credentials are absent
                });
            }
        };

        const applyDestructiveData = () => {
            const currentState = useStore.getState();

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const batch: Record<string, any> = {};
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const profilePatch: Record<string, any> = {};

            // STATE PRESERVATION: In non-interactive mode (login / session restore),
            // do NOT overwrite a non-empty local plan with an empty cloud plan.
            // This prevents state destruction when:
            //   - Cloud upload partially failed (plan missing from cloud)
            //   - Download triggers during in-progress onboarding
            //   - Email verification re-triggers sync before plan reached cloud
            // In interactive mode the conflict dialog already handles this.
            const localPlanExists = Array.isArray(currentState.plan) && currentState.plan.length > 0;
            const cloudPlanExists = Array.isArray(cloudPlan) && cloudPlan.length > 0;

            if (cloudPlanExists || !localPlanExists) {
                batch.plan = cloudPlan || [];
                profilePatch.plan = cloudPlan || [];
            } else {
                // Non-interactive & local plan exists but cloud is empty — preserve local.
                log.warn('hydrate', 'Preserving local plan — cloud plan is empty during non-interactive sync.', {
                    localWeeks: currentState.plan.length,
                });
            }

            if (cloudGoalRaceDate) {
                batch.goal = { ...currentState.goal, raceDate: cloudGoalRaceDate };
                profilePatch.goal = batch.goal;
            } else if (batch.plan !== undefined && batch.plan.length === 0) {
                // Only clear goal race date if we actually applied an empty cloud plan
                batch.goal = { ...currentState.goal, raceDate: null };
                profilePatch.goal = batch.goal;
            }

            // Same preservation logic for blocks: don't wipe non-empty local blocks
            // with empty cloud blocks during non-interactive sync.
            const localBlocksExist = Array.isArray(currentState.blocks) && currentState.blocks.length > 0;
            const cloudBlocksExist = Array.isArray(cloudBlocks) && cloudBlocks.length > 0;

            if (cloudBlocksExist || !localBlocksExist) {
                batch.blocks = cloudBlocks || [];
                profilePatch.blocks = cloudBlocks || [];
            } else {
                log.warn('hydrate', 'Preserving local blocks — cloud blocks are empty during non-interactive sync.', {
                    localBlocks: currentState.blocks.length,
                });
            }

            log.info('hydrate', 'Plan hydrated', { weeks: batch.plan.length });

            // Update profiles array once
            const profileToUpdate = currentState.profiles.find(p => p.id === userId);
            if (profileToUpdate && Object.keys(profilePatch).length > 0) {
                batch.profiles = currentState.profiles.map(p =>
                    p.id === userId
                        ? { ...p, ...profilePatch, isHydrated: true }
                        : p
                );
            }

            if (Object.keys(batch).length > 0) {
                // Apply to top-level state if this is the active profile, OR if no
                // profile is active yet (fresh login from incognito/new device).
                const shouldApplyToTopLevel = currentState.activeProfileId === userId ||
                    (!currentState.activeProfileId && !isImpersonating);

                if (shouldApplyToTopLevel) {
                    if (!currentState.activeProfileId) {
                        batch.activeProfileId = userId;
                    }
                    useStore.setState(batch);
                } else if (batch.profiles) {
                    useStore.setState({ profiles: batch.profiles });
                }
            }

            log.info('hydrate', 'Destructive cloud data applied.');
        };

        // ================================================================
        // DECISION: Show warning or apply immediately
        // ================================================================
        // Always apply non-destructive data (profile, settings, credentials)
        // immediately — this prevents forced onboarding redirects while
        // the user is deciding on plan conflicts.
        await applyNonDestructiveData(interactive);

        const hasDestructiveChanges = changes.some(c => c.severity === 'destructive' || c.severity === 'warning');

        if (interactive && hasDestructiveChanges && changes.length > 0) {
            // Show the warning dialog and let the user decide about plan/blocks only
            log.info('hydrate', 'Detected conflicting changes, showing warning dialog', { changeCount: changes.length, fields: changes.map(c => c.field) });
            store.setPendingSync({
                active: true,
                source: 'Cloud Sync',
                changes,
                applyFn: applyDestructiveData,
            });
        } else {
            // No significant conflicts — apply plan/blocks silently
            applyDestructiveData();
        }

    } catch (err) {
        if (err instanceof ExternalServiceError) {
            log.error('hydrate', `Hydration failed: ${err.message}`, {
                code: err.code,
                context: err.context,
                cause: err.cause
            });
        } else {
            log.error('hydrate', 'Hydration failed', { error: err instanceof Error ? err.message : String(err) });
        }
    } finally {
        _isHydrating = false;
        useStore.getState().setHydrating(false);
    }
};

// ============================================================================
// ANONYMOUS SUBMISSION LINKING
// ============================================================================

/**
 * Links an anonymous onboarding submission to a newly registered user.
 * The submission ID is stored in transient storage during guest onboarding.
 */
const linkAnonymousSubmission = async (userId: string) => {
    try {
        const submissionId = getTransientItem(ANONYMOUS_SUBMISSION_ID_KEY);
        if (!submissionId) return;

        const { error } = await supabase.rpc('link_anonymous_submission', {
            p_submission_id: submissionId,
            p_user_id: userId,
        });

        if (error) {
            log.warn('linkSubmission', 'Failed to link anonymous submission (non-blocking)', { error });
        } else {
            log.info('linkSubmission', 'Linked anonymous onboarding submission to user', { userId });
        }

        removeTransientItem(ANONYMOUS_SUBMISSION_ID_KEY);
    } catch (err) {
        if (err instanceof ExternalServiceError) {
            log.warn('linkSubmission', `Error linking anonymous submission: ${err.message}`, {
                code: err.code,
                context: err.context
            });
        } else {
            log.warn('linkSubmission', 'Error linking anonymous submission', { error: err instanceof Error ? err.message : String(err) });
        }
    }
};

/**
 * Fetches all athletes coached by the current user and adds them to the store.
 */
const fetchCoachedProfiles = async (coachId: string) => {
    log.info('fetchCoachedProfiles', 'Fetching coached athletes for coach', { coachId });
    try {
        // Query profiles where coach_id equals the current user's ID
        const { data: serverAthletes, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('coach_id', coachId);

        if (error) throw error;

        // Defensive guard: never treat the authenticated coach's own profile as coached.
        // If coach_id was incorrectly set to self on the server, this excludes it.
        const athletes = (serverAthletes || []).filter(athlete => athlete.id !== coachId);
        const store = useStore.getState();
        const serverIds = new Set(athletes.map(a => a.id));

        // 1. RECONCILIATION: Remove local coached profiles no longer on the server.
        // This also clears stale "coached self" entries that can cause false
        // impersonation banners and wrong active-profile targeting.
        const profilesToRemove = store.profiles.filter(p =>
            p.relation === 'coached' && !serverIds.has(p.id)
        );

        if (profilesToRemove.length > 0) {
            log.info('fetchCoachedProfiles', 'Removing stale coached profiles from local store', { count: profilesToRemove.length });
            useStore.setState((state) => {
                const nextProfiles = state.profiles.filter(
                    p => !(p.relation === 'coached' && !serverIds.has(p.id))
                );

                const activeExists = state.activeProfileId
                    ? nextProfiles.some(p => p.id === state.activeProfileId)
                    : false;
                const nextActiveProfileId = activeExists
                    ? state.activeProfileId
                    : (nextProfiles.find(p => p.relation === 'self')?.id || nextProfiles[0]?.id || null);

                // STATE PRESERVATION: If the active profile was removed, restore
                // the fallback profile's data to top-level state. Without this,
                // the coached athlete's data lingers as the coach's active state
                // even though activeProfileId now points to the self profile.
                if (!activeExists && nextActiveProfileId) {
                    const fallbackProfile = nextProfiles.find(p => p.id === nextActiveProfileId);
                    if (fallbackProfile && fallbackProfile.isHydrated) {
                        const restoredData = extractProfileData(fallbackProfile);
                        return {
                            ...restoredData,
                            profiles: nextProfiles,
                            activeProfileId: nextActiveProfileId,
                            // Clear impersonation state since we're falling back to self
                            isImpersonating: false,
                            impersonatedUserId: null,
                            isSupportImpersonation: false,
                        };
                    }
                }

                return {
                    profiles: nextProfiles,
                    activeProfileId: nextActiveProfileId,
                };
            });
        }

        // 2. Add or Update remaining
        if (athletes.length > 0) {
            log.info('fetchCoachedProfiles', 'Found coached athletes on server', { count: athletes.length });

            athletes.forEach(athlete => {
                // Check if already exists to avoid duplicates
                const existing = useStore.getState().profiles.find(p => p.id === athlete.id);
                if (!existing) {
                    log.info('fetchCoachedProfiles', 'Syncing new coached athlete', { displayName: athlete.display_name || athlete.id });
                    store.addCoachedProfile(
                        athlete.intervals_athlete_id || '',
                        athlete.display_name || 'Unnamed Athlete',
                        athlete.sport || 'Running',
                        {
                            explicitId: athlete.id,
                            coachId: athlete.coach_id ?? coachId,
                        }
                    );
                }

                if (!existing || existing.relation === 'coached') {
                    // Keep coached profile identity metadata aligned with server.
                    // This prevents local stale IDs/athlete IDs from targeting wrong rows.
                    useStore.setState(state => ({
                        profiles: state.profiles.map(p => {
                            if (p.id !== athlete.id || p.relation !== 'coached') return p;

                            return {
                                ...p,
                                displayName: athlete.display_name || p.displayName,
                                intervalsAthleteId: athlete.intervals_athlete_id || p.intervalsAthleteId,
                                coachId: athlete.coach_id ?? p.coachId,
                                connectedAt: athlete.connected_at || p.connectedAt || null,
                            };
                        })
                    }));
                }
            });
        }
    } catch (err) {
        if (err instanceof ExternalServiceError) {
            log.error('fetchCoachedProfiles', `Failed to fetch coached profiles: ${err.message}`, {
                code: err.code,
                context: err.context
            });
        } else {
            log.error('fetchCoachedProfiles', 'Failed to fetch coached profiles', { error: err instanceof Error ? err.message : String(err) });
        }
    }
};

// ============================================================================
// SMART SYNC (First Load Logic)
// ============================================================================

/**
 * Intelligent sync on first load / login.
 * Distinguishes between:
 * 1. New User (Guest -> User): Has local data but no cloud profile. Action: PUSH local -> cloud.
 * 2. Existing User (New Device): Has cloud profile. Action: PULL cloud -> local.
 */
export const syncOnFirstLoad = async (userId: string) => {
    log.info('smartSync', 'Initiating Smart Sync for user', { userId });
    beginBootstrapSyncForUser(userId);
    let bootstrapSucceeded = false;

    // ONBOARDING GUARD: If the user is on the onboarding page with local
    // wizard progress, they are mid-flow. A bare /onboarding route can also
    // be an existing-user login from a fresh browser, so do not skip cloud
    // hydration unless there is real local onboarding state to preserve.
    const isOnboardingRoute = typeof window !== 'undefined' &&
        window.location.pathname.includes('/onboarding');
    const isOnboardingActive = isOnboardingRoute && hasActiveOnboardingProgress();

    try {
        // Link any anonymous onboarding submission to this user
        await linkAnonymousSubmission(userId);

        const { data: profile, error } = await supabase
            .from('profiles')
            .select('id, display_name, intervals_athlete_id')
            .eq('id', userId)
            .maybeSingle();

        if (error) {
            log.error('smartSync', 'Error checking profile existence', { error });
            // On error, attempt non-interactive hydration — but respect onboarding guard
            if (!isOnboardingActive) {
                await downloadCloudDataToLocal(userId, false);
            } else {
                log.info('smartSync', 'Skipping error-path download — user is mid-onboarding.');
            }
            return;
        }

        const localState = useStore.getState();
        const localAthleteName = localState.athlete.name;
        const localHasPlan = localState.plan && localState.plan.length > 0;

        if (profile) {
            // CASE 1: Profile exists in Cloud (Normal Login / Return User)
            // POLICY: Cloud is Truth.

            // Check if this is a "Fresh" profile that needs initial hydration from local
            // (Only happens if user created account but upload failed previously, OR strict onboarding flow)
            if (!profile.display_name && localAthleteName) {
                // Fresh cloud profile (empty name) + local data => push local to cloud
                log.info('smartSync', 'Fresh Cloud Profile detected with Local Data. Pushing local to cloud.');
                await uploadLocalDataToCloud({ includePlan: true, includeSettings: true, includeProfile: true, allowDuringBootstrap: true });
            } else {

                // **SMART MERGE LOGIC**
                // If Local Plan exists AND Cloud Plan exists, we have a conflict.
                let cloudHasPlan = false;
                let cloudPlanStructure: WeekSchedule[] | null = null;

                // The merge check is only relevant for guest-onboarding plans.
                // Skipping the fetch otherwise removes a duplicate query from
                // the bootstrap path — downloadCloudDataToLocal fetches the
                // active plan again anyway.
                const isGuestPlan = localState.isGuestOnboardingPlan;
                if (localHasPlan && isGuestPlan) {
                    const { data: activePlans, error: activePlanError } = await supabase
                        .from('training_plans')
                        .select('id,plan_structure,updated_at,created_at')
                        .eq('user_id', userId)
                        .eq('status', 'active');

                    if (activePlanError) {
                        log.error('smartSync', 'Error fetching active plan for merge check', { error: activePlanError });
                    }

                    if (activePlans && activePlans.length > 0) {
                        const { primary, duplicates } = selectCanonicalActivePlan(activePlans);
                        if (duplicates.length > 0) {
                            void archiveDuplicateActivePlans(duplicates.map(p => p.id));
                        }

                        if (primary?.plan_structure) {
                            cloudHasPlan = true;
                            cloudPlanStructure = parseWeekScheduleArray(primary.plan_structure);
                        }
                    }
                }

                // STRICTER CONFLICT DETECTION:
                // Only trigger the merge dialog if:
                // 1. Local plan exists AND was created via guest onboarding (isGuestOnboardingPlan)
                // 2. Cloud plan exists
                // 3. Plans are actually different
                if (localHasPlan && cloudHasPlan && isGuestPlan) {
                    // ONLY trigger dialog if they are actually DIFFERENT
                    if (!plansAreDifferent(localState.plan, cloudPlanStructure || [])) {
                        log.info('smartSync', 'Local and Cloud plans are identical. Skipping merge dialog.');
                        // Clear the flag since we're not showing the dialog
                        useStore.getState().setGuestOnboardingPlan(false);
                    } else {
                        log.info('smartSync', 'Conflict Detected: Guest Plan vs Cloud Plan. Triggering Smart Merge.');

                        // 1. Define Keep Cloud Action (with proper cleanup)
                        const onKeepCloud = async () => {
                            log.info('smartSync', 'User chose: Keep Cloud. Clearing local data.');

                            // Clear the guest onboarding flag FIRST
                            useStore.getState().setGuestOnboardingPlan(false);

                            // Clear local cache copies to prevent stale data resurfacing on refresh
                            try {
                                if (typeof window !== 'undefined') {
                                    window.sessionStorage.removeItem('adaptive-endurance-storage-v1');
                                    window.localStorage.removeItem('adaptive-endurance-storage-v1');
                                }
                                const { del } = await import('idb-keyval');
                                await del('adaptive-endurance-storage-v1');
                                log.info('smartSync', 'Local cache cleared successfully.');
                            } catch (e) {
                                // SILENT: idb-keyval cleanup failure is non-critical; session/localStorage already cleared above
                                log.warn('smartSync', 'Local cache cleanup failed (idb-keyval)', { error: e instanceof Error ? e.message : String(e) });
                            }

                            // Reset store to initial state before hydrating from cloud
                            useStore.getState().reset();

                            // Now hydrate from cloud
                            await downloadCloudDataToLocal(userId, false);
                            // Conflict resolved -> allow regular cloud writes again.
                            markBootstrapReadyForUser(userId);
                        };

                        // 2. Define Keep Local Action
                        const onKeepLocal = async (options: { plan: boolean; settings: boolean; profile: boolean }) => {
                            log.info('smartSync', 'User chose: Keep Local. Overwriting Cloud partially.', { options });

                            // Clear the flag since user is keeping local
                            useStore.getState().setGuestOnboardingPlan(false);

                            await uploadLocalDataToCloud({
                                includePlan: options.plan,
                                includeSettings: options.settings,
                                includeProfile: options.profile,
                                allowDuringBootstrap: true
                            });
                            // After upload, we pull back to ensure sync state is clean
                            await downloadCloudDataToLocal(userId, false);
                            // Conflict resolved -> allow regular cloud writes again.
                            markBootstrapReadyForUser(userId);
                        };

                        // 3. Trigger Dialog
                        useStore.getState().setMergeConflict({
                            active: true,
                            localSummary: summarizePlan(localState.plan), // Use actual summary
                            cloudSummary: summarizePlan(cloudPlanStructure || []), // Use actual summary
                            onKeepCloud,
                            onKeepLocal,
                        });

                        // Populate the coach switcher in the background while
                        // the user resolves the conflict.
                        void fetchCoachedProfiles(userId);

                        return; // Stop here, wait for user input
                    }
                }

                if (isOnboardingActive) {
                    // ONBOARDING GUARD: User is mid-onboarding. Downloading cloud data
                    // now would overwrite in-progress wizard state (athlete, availability,
                    // settings) with stale/empty cloud defaults.
                    // We still fetch subscription & feature flags below to ensure
                    // access control works, but skip the destructive download.
                    log.info('smartSync', 'Skipping cloud download — user is mid-onboarding. Will sync on completion.');
                } else {
                    log.info('smartSync', 'Existing Cloud Profile found. Enforcing Cloud Truth.');
                    await downloadCloudDataToLocal(userId, false);
                }

                // CHECK FOR COACHED ATHLETES (If I am a coach).
                // Runs after hydration (it patches the same profiles array)
                // but off the critical path — the switcher populates a moment
                // later instead of blocking first paint.
                void fetchCoachedProfiles(userId);

                // Ensure "Self" profile exists in the switcher (POST-HYDRATION CHECK)
                const store = useStore.getState();
                const selfProfileExists = store.profiles.some(p => p.relation === 'self');
                if (!selfProfileExists && profile) {
                    log.info('smartSync', 'Ensuring Self profile exists in store (Post-Hydration).');
                    store.createSelfProfile(
                        profile.intervals_athlete_id || '',
                        profile.display_name || 'Me',
                        { setActive: false }
                    );
                }
            }
        } else {
            // CASE 2: No Cloud Profile (Brand New User via Signup)
            // Action: Push current Guest state to Cloud
            log.info('smartSync', 'No Cloud Profile found. Migrating Guest data to Cloud.');
            await uploadLocalDataToCloud({ includePlan: true, includeSettings: true, includeProfile: true, allowDuringBootstrap: true });

            // MANDATORY: After migrating guest data to cloud, we MUST establish a local profile
            // otherwise the ProfileSwitcher remains empty and user feels "not logged in".
            const store = useStore.getState();
            if (!store.profiles.some(p => p.relation === 'self')) {
                log.info('smartSync', 'Creating local Self profile for new user.');
                store.createSelfProfile(
                    store.intervals.athleteId || 'pending',
                    store.athlete.name || 'Me',
                    { setActive: true }
                );
            }
        }

        // ALWAYS: Sync Subscription & Feature Flags (Critical for Access Control)
        await syncSubscriptionData(userId);

        // Trigger incremental activity sync on login without blocking app bootstrap.
        void runIncrementalActivitySync(userId).catch((error) => {
            log.warn('smartSync', 'Incremental activity sync failed', { error, userId });
        });
        bootstrapSucceeded = true;

    } catch (err) {
        if (err instanceof ExternalServiceError) {
            log.error('smartSync', `Smart Sync failed: ${err.message}`, {
                code: err.code,
                context: err.context
            });
        } else {
            log.error('smartSync', 'Smart Sync failed', { error: err instanceof Error ? err.message : String(err) });
        }
    } finally {
        endBootstrapSyncForUser(userId, { succeeded: bootstrapSucceeded });
    }
};

// ============================================================================
// AUTO SYNC
// ============================================================================

let syncTimer: ReturnType<typeof setTimeout> | null = null;
const SYNC_DEBOUNCE_MS = 2000; // 2 seconds debounce

// Hydration guard: prevents auto-sync from firing during cloud → local hydration.
// Without this, each individual store update triggers the auto-sync subscriber,
// causing cascading state updates that exceed React's maximum update depth.
let _isHydrating = false;

/**
 * Initializes auto-sync listener on store changes.
 * Only syncs when meaningful data (plan, athlete, metrics) changes.
 */
export const initAutoSync = () => {
    log.info('autoSync', 'Initializing Auto-Sync.');

    // Whitelist: only these fields represent data persisted to the cloud.
    // Any state change NOT in this list is UI-only and should not trigger sync.
    const SYNC_TRIGGER_FIELDS = [
        'athlete', 'metrics', 'goal', 'events',
        'plan', 'blocks', 'availability', 'longRunDay',
        'progression', 'cyclingProgression', 'config',
        'preferences', 'gamification', 'planState', 'planName',
        'enabledSports', 'triathlonProgression',
        'intervals', 'strava', 'stravaConfig', 'planSettings',
        'userRole', 'consent', 'discovery',
    ] as const;

    return useStore.subscribe((state, prevState) => {
        const authUserId = state.authUser?.id ?? null;
        if (!canWriteCloudForUser(authUserId)) return;

        // Skip entirely during cloud → local hydration to prevent cascading
        // state updates that cause React error #185 (max update depth exceeded)
        if (_isHydrating || state.isHydrating) return;

        // Whitelist approach: only trigger sync when persisted data fields change.
        // This prevents excessive uploads from UI interactions (selectedWeek,
        // viewMode, dragState, discovery, stats, etc.).
        const hasDataChanged = SYNC_TRIGGER_FIELDS.some(
            key => state[key as keyof typeof state] !== prevState[key as keyof typeof prevState]
        );
        if (!hasDataChanged) return;

        // Fresh authenticated sessions (incognito/new device) can emit interim
        // default-state updates before profile bootstrap completes. If we upload
        // at this point, defaults may overwrite cloud profile data.
        const hasAuthUser = Boolean(state.authUser?.id);
        const hasBootstrappedProfile = Boolean(
            state.activeProfileId || state.profiles.some(p => p.id === state.authUser?.id)
        );
        if (hasAuthUser && !hasBootstrappedProfile) return;

        // ONBOARDING GUARD: During onboarding, the store receives incremental
        // updates (athlete name, sport, availability) before the plan is generated.
        // Auto-syncing these partial writes would pollute the cloud profile with
        // incomplete defaults that a subsequent download could restore destructively.
        // The onboarding handleComplete performs an explicit uploadLocalDataToCloud
        // with all data at once, making auto-sync during onboarding redundant.
        if (typeof window !== 'undefined' && window.location.pathname.includes('/onboarding')) {
            return;
        }

        if (syncTimer) clearTimeout(syncTimer);

        syncTimer = setTimeout(async () => {
            log.info('autoSync', 'Auto-sync triggering.');
            try {
                await uploadLocalDataToCloud();
            } catch (err) {
                log.error('autoSync', 'Auto-sync failed', { error: err instanceof Error ? err.message : String(err) });
            }
        }, SYNC_DEBOUNCE_MS);
    });
};

/**
 * Updates a global feature flag in the database.
 * This affects all users in the system.
 */
export const updateGlobalFeatureFlag = async (key: string, updates: Partial<FeatureFlag>) => {
    log.info('updateFlag', 'Updating global feature flag', { key, updates });

    // Build only the columns we actually want to change (omit undefined fields)
    const patch: Record<string, unknown> = {
        feature_key: key,
        updated_at: new Date().toISOString()
    };
    if (updates.isEnabled !== undefined) patch.is_enabled = updates.isEnabled;
    if (updates.description !== undefined) patch.description = updates.description;
    if (updates.isProOnly !== undefined) patch.is_pro_only = updates.isProOnly;
    if (updates.minTier !== undefined) patch.min_tier = updates.minTier;
    if (updates.accessLevel !== undefined) patch.access_level = updates.accessLevel;

    const { error } = await supabase
        .from('feature_flags')
        .upsert(patch, { onConflict: 'feature_key' });

    if (error) {
        log.error('updateFlag', 'Failed to upsert global feature flag', { key, error });
        throw error;
    }
};

/**
 * Syncs Subscription, Feature Flags, and Referral data.
 * This is "Authoritative" data that dictates app access.
 */
export const syncSubscriptionData = async (userId: string) => {
    log.info('syncSubscription', 'Syncing Subscription & Access Data.', { userId });
    try {
        // Subscription, feature flags, referral code and referral records are
        // independent — fetch them in parallel instead of four sequential
        // round-trips (part of the slow-bootstrap fix).
        const [subResult, , profileResult, referralResult] = await Promise.all([
            supabase.from('subscriptions').select('*').eq('user_id', userId).single(),
            syncPublicFeatureFlags().catch((flagsError) => {
                log.error('syncSubscription', 'Failed to fetch feature flags', { error: flagsError });
            }),
            supabase.from('profiles').select('referral_code').eq('id', userId).single(),
            supabase
                .from('referrals')
                .select('id, referred_user_id, referral_code, status, referrer_reward_months, referrer_reward_claimed, created_at')
                .eq('referrer_id', userId)
                .order('created_at', { ascending: false }),
        ]);

        const { data: subData, error: subError } = subResult;
        if (subError && subError.code !== 'PGRST116') { // PGRST116 is "Row not found"
            log.error('syncSubscription', 'Failed to fetch subscription', { error: subError });
        }

        if (subData) {
            useStore.getState().setSubscription({
                status: subData.status as SubscriptionStatus,
                tier: subData.tier as SubscriptionTier,
                currentPeriodEnd: subData.current_period_end,
                isPro: subData.status === 'active' || ['lifetime', 'pro', 'donation'].includes(subData.tier),
                proSource: (subData.source as ProSource) || null,
            });
        }

        const referralCode = profileResult.data?.referral_code || null;

        const { data: referralData, error: referralError } = referralResult;
        if (referralError) {
            log.error('syncSubscription', 'Failed to fetch referrals', { error: referralError });
        }

        const referrals = referralData || [];
        const totalMonthsEarned = referrals.reduce(
            (sum: number, r: { referrer_reward_months: number }) => sum + (r.referrer_reward_months || 0),
            0
        );

        useStore.getState().setReferrals({
            code: referralCode,
            totalReferrals: referrals.length,
            monthsEarned: totalMonthsEarned,
            referrals: referrals.map((r: { id: string; referred_user_id: string; status: string; created_at: string; referrer_reward_claimed: boolean }) => ({
                id: r.id,
                referredUserId: r.referred_user_id,
                status: r.status as 'pending' | 'converted',
                createdAt: r.created_at,
                rewardClaimed: r.referrer_reward_claimed,
            })),
        });

    } catch (err) {
        if (err instanceof ExternalServiceError) {
            log.error('syncSubscription', `Subscription sync failed: ${err.message}`, {
                code: err.code,
                context: err.context
            });
        } else {
            log.error('syncSubscription', 'Subscription sync failed', { error: err instanceof Error ? err.message : String(err) });
        }
    }
};

// ============================================================================
// EXTERNAL ACTIVITY SYNC
// ============================================================================

/**
 * Syncs Strava activities to the cloud database.
 * Fetches recent activities from Strava -> Ingests via Golden Record Service -> Matches to Plan.
 */
export const syncStravaActivities = async (userId: string) => {
    // Check if Strava is enabled/connected in state
    const state = useStore.getState();
    if (!state.strava.accessToken) {
        log.warn('syncStrava', 'Strava not connected. Skipping sync.');
        return { ingested: 0, matched: 0 };
    }

    log.info('syncStrava', 'Starting Strava Sync...', { userId });

    // Ingest (Fetch -> Dedupe -> Golden Record)
    const ingestedCount = await runIncrementalActivitySync(userId);

    // Match to Plan
    const matchedCount = await stravaActivityService.matchActivities(userId);

    // Calculate Compliance
    await adaptivePlanner.calculateCompliance(userId);

    // [New] Waterfall Shift: Check for missed KEY sessions and reschedule
    log.info('syncStrava', 'Checking for missed key workouts (Waterfall Shift)...');
    try {
        const shiftedCount = await adaptivePlanner.checkAndApplyWaterfallShift();
        if (shiftedCount > 0) {
            log.info('syncStrava', `Waterfall Shift Applied: moved ${shiftedCount} key workout(s).`);
            // If plan changed, we need to trigger a cloud sync?
            // The method updates local store state.syncStravaActivities is called usually by UI or auto-sync?
            // syncStravaActivities is called by UI.
            // If local state updated, auto-sync listener in cloudSync.ts will pick it up and push to cloud.
        } else {
            log.info('syncStrava', 'No key workouts needed shifting.');
        }
    } catch (error) {
        log.error('syncStrava', 'Waterfall Shift failed', { error });
    }

    log.info('syncStrava', 'Strava Sync Complete', { ingestedCount, matchedCount });
    return { ingested: ingestedCount, matched: matchedCount };
};

/**
 * Locks historical activities older than the threshold (T-24h).
 * This prevents automatic updates from changing the physiological load of past activities.
 */
export const lockHistory = async (athleteId: string) => {
    const { error } = await supabase.rpc('lock_historical_activities', {
        p_athlete_id: athleteId,
        p_lock_threshold_hours: 24
    });
    if (error) {
        log.error('lockHistory', 'Failed to lock historical activities', { error });
        throw error;
    }
    log.info('lockHistory', 'Historical activities locked successfully');
};
